import anime from 'animejs/lib/anime.es.js';
import Splitting from 'splitting';
import scrollLock from 'scroll-lock';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { scrollPageTo } from '../utils';
import { getViewPercentage } from '../utils';

export default class ChapterPart {
	static create(chapter) {
		const chapterParts = chapter.content.querySelectorAll('[data-chapter-part]');

		if (!chapterParts.length) {
			return null;
		}

		return Array.from(chapterParts).map(chapterPart => {
			return new ChapterPart(chapterPart, chapter);
		});
	}

	constructor(el, chapter) {
		this.breakpoint = window.matchMedia('(max-width:767px)');

		this.el = el;
		anime.set(this.el, {
			translateY: this.breakpoint.matches ? window.outerHeight+'px' : window.innerHeight+'px', // Outer
			opacity: 1,
		});
		this.index = this.el.getAttribute('data-chapter-part');
		this.chapter = chapter;

		this.chapterPartDataElement = this.el.querySelector('[data-chapter-part-data]');
		this.data = JSON.parse(this.chapterPartDataElement.innerHTML);
		this.partModules = this.el.querySelector('[data-part-modules]');
		this.partModulesElms = this.partModules.querySelectorAll('[data-part-module]');
		this.partModulesElms.forEach(partModuleElm => { // Rigorously remove 'unused' modules
			if(this.breakpoint.matches) {
				if(partModuleElm.classList.contains('pllntrs-chapter-module--hide-on-mobile')) {
					partModuleElm.remove();
				}
			} else {
				if(partModuleElm.classList.contains('pllntrs-chapter-module--hide-on-desktop')) {
					partModuleElm.remove();
				}
			}
		});
		this.activeModule = this.partModulesElms[0];

		this.partIntrotype = this.chapter.content.querySelector('[data-chapter-part-intro-type="'+this.index+'"]');
		this.introTypePartHeader = this.partIntrotype.querySelector('[data-intro-type-part-header]');
		this.partNumber = this.partIntrotype.querySelector('[data-intro-type-part-number]');
		if(!this.breakpoint.matches) {
			this.splitPartNumber = Splitting({
				target: this.partNumber,
				by: 'chars'
			});
		}
		this.partTitle = this.partIntrotype.querySelector('[data-intro-type-part-title]');
		if(!this.breakpoint.matches) {
			this.splitPartTitle = Splitting({
				target: this.partTitle,
				by: 'chars'
			});
		}
		this.partTitleOutPosition = this.introTypePartHeader.getAttribute('data-part-title-out-position');

		this.nextIndex = parseInt(this.index)+1;
		this.nextPart = this.chapter.content.querySelector('[data-chapter-part="'+this.nextIndex+'"]');
		this.nextPartIntroType = this.chapter.content.querySelector('[data-chapter-part-intro-type="'+this.nextIndex+'"]');
		if(this.nextPartIntroType) {
			this.progressBar = this.nextPartIntroType.querySelector('[data-next-part-progress-bar]');
		}

		this.nextPartScroll = this.el.querySelector('[data-next-part-scroll]');
		this.inlineCredits = this.el.querySelector('[data-inline-credits]');
		if(this.inlineCredits) {
			this.lastChapter = true;
		}
		if(!this.nextPartScroll&&this.inlineCredits) {
			this.nextPartScroll = this.inlineCredits;
		}
		this.titlesShown = false;

		this.el.addEventListener('click', e => {
			this.scrollToNextModule();
		});

		if (this.active) {
			this.init();
		}
	}

	init() {
		this.el.classList.add('is-active');
		window.scrollTo(0, 0);
		anime.set(this.el, {
			translateY: 0,
			opacity: 1,
		});

		scrollLock.clearQueueScrollLocks();
		scrollLock.enablePageScroll();

		this.chapter.audio.progressBar.value = 0;
		this.chapter.audio.audioElement.volume = this.chapter.audio.volume;

		this.boundResizeListener = debounce(this.handleResize.bind(this), 100);
		window.addEventListener('resize', this.boundResizeListener);
		this.boundResizeListener();

		this.prevScroll = 0;
		this.boundScrollListener = this.handleScroll.bind(this);
		document.addEventListener('scroll', this.boundScrollListener);

		this.boundThrotteledScrollListener = throttle(this.handleThrotteledScroll.bind(this), 300);
		document.addEventListener('scroll', this.boundThrotteledScrollListener);
		this.handleThrotteledScroll();

		// Only show part titles if part doesn't get triggered by the Preloader
		if(this.chapter.preloader.loadingProgress!=0) {
			this.introTypePartHeader.classList.add('show-episode-number');

			const currentProgressBar = this.introTypePartHeader.querySelector('[data-next-part-progress-bar]');
			if(currentProgressBar) {
				anime.set(currentProgressBar, {
					scaleX: 1
				});
			}
			this.showTitles();
		}

		//this.chapter.scrollIndicator.classList.remove('is-hidden');
	}

	scrollToNextModule() {
		if(this.activeModule) {
			const nextModule = this.activeModule.nextElementSibling;
			if(nextModule) {
				scrollPageTo(nextModule, 2000);
			} else { // Scroll to next Part
				let nextPartScrollHeight = 0;
				if (this.nextPartScroll) {
					nextPartScrollHeight = this.nextPartScroll.clientHeight;
					scrollPageTo(this.activeModule, 2000, this.activeModule.scrollHeight+(nextPartScrollHeight));
				}
			}
		}
	}

	setupAnimations() {
		if(!this.lastChapter) {
			this.prevPartAnimation = anime({
				targets: this.el,
				opacity: [1, 0.15],
				duration: 1000,
				easing: 'easeOutCubic',
				autoplay: false,
			});
		}

		if(this.lastChapter) {
			this.lastChapterAnimation = anime({
				targets: this.partModules,
				opacity: [1, 0.15],
				duration: 1000,
				easing: 'easeOutCubic',
				autoplay: false,
				changeComplete: anim => {
					if(anim.progress===0) {
						this.chapter.elm.classList.remove('index-shown');
						this.chapter.elm.classList.add('show-header-ui');
					}
				},
				changeBegin: () => {
					this.chapter.elm.classList.add('index-shown');
					this.chapter.elm.classList.remove('show-header-ui');
				}
			});
		}

		if (this.chapter.lastChapterPartActive) {
			return;
		}

		this.nextPartAnimation = anime({
			targets: this.nextPart,
			translateY: this.breakpoint.matches ? [window.outerHeight+'px', window.outerHeight * 0.25 + 'px'] : [window.innerHeight+'px', window.innerHeight * 0.25 + 'px'], // Outer
			duration: 1000,
			// easing: 'easeOutSine',
			easing: 'linear',
			autoplay: false,
			changeComplete: anim => {
				if(anim.progress===0) {
					this.chapter.parts[this.nextIndex].hideTitles();
				}
			},
			changeBegin: () => {
				this.chapter.parts[this.nextIndex].showTitles();
			},
			complete: () => {
				this.animateToNextPart();
			}
		});

		this.progressBarAnimation = anime({
			targets: this.progressBar,
			scaleX: [0, 1],
			duration: 1000,
			easing: 'linear',
			autoplay: false,
			begin: () => {
				this.progressBar.style.transformOrigin = 'left';
				this.nextPartIntroType.classList.add('show-episode-number');
			}
		});
	}

	showTitles() {
		if(this.titlesShown) {
			return;
		}

		anime.set(this.progressBar, {
			scaleX: 0
		});

		this.titlesShown = true;
		this.introTypePartHeader.classList.remove('is-hidden');
		if(!this.breakpoint.matches) { // Desktop
			anime.remove([this.splitPartNumber[0].chars, this.splitPartTitle[0].chars]);
		} else {
			anime.remove([this.partNumber, this.partTitle]);
		}

		anime.set(this.partIntrotype, {
			opacity: 1
		});
		anime.set(this.introTypePartHeader, {
			opacity: 1
		});
		if(!this.breakpoint.matches) {
			anime.set(this.splitPartNumber[0].chars, {
				opacity: 0
			});
			anime.set(this.splitPartTitle[0].chars, {
				opacity: 0
			});
			anime({
				targets: this.splitPartNumber[0].chars,
				opacity: 1,
				duration: 1,
				easing: 'linear',
				delay: anime.stagger(50),
				complete: () => {
					anime({
						targets: this.splitPartTitle[0].chars,
						opacity: 1,
						duration: 1,
						easing: 'linear',
						delay: anime.stagger(50, {start: 50}),
					});
				},
			});
		} else { // Mobile
			anime.set(this.partNumber, {
				opacity: 0
			});
			anime.set(this.partTitle, {
				opacity: 0
			});
			anime({
				targets: this.partNumber,
				opacity: 1,
				duration: 1000,
				easing: 'linear',
				complete: () => {
					anime({
						targets: this.partTitle,
						opacity: 1,
						duration: 1000,
						easing: 'linear',
					});
				},
			});
		}
	}

	hideTitles() {
		if(!this.titlesShown) {
			return;
		}
		this.titlesShown = false;
		this.introTypePartHeader.classList.add('is-hidden');
	}

	animateToNextPart() {
		if (!this.active) {
			return;
		}

		if (this.chapter.lastChapterPartActive) {
			return;
		}

		anime({
			targets: this.el,
			opacity: 0,
			duration: 1500,
			easing: 'easeInOutCubic',
		});

		anime({
			targets: this.nextPart,
			translateY: 0 + 'px',
			opacity: 1,
			duration: 1500,
			easing: 'easeInOutCubic',
			begin: () => {
				scrollLock.disablePageScroll();
			},
			complete: () => {
				this.chapter.goToNextPart();
			},
		});

	}

	handleResize() {
		if (!this.active) {
			return;
		}

		this.setupAnimations();

		// set top offset for sticky modules
		anime.set(this.partModules, {
			top: (this.partModules.clientHeight - window.innerHeight) * -1 + 'px',
		});

		let nextPartScrollHeight = 0;
		if (this.nextPartScroll) {
			nextPartScrollHeight = this.nextPartScroll.clientHeight;
		}

		this.height = document.documentElement.scrollHeight - document.documentElement.clientHeight - 100;
		this.height2 = document.documentElement.scrollHeight - document.documentElement.clientHeight - (nextPartScrollHeight * 1);
		this.restHeight = this.height2 - this.height;

		this.newHeight2 = document.documentElement.scrollHeight - document.documentElement.clientHeight - (nextPartScrollHeight * 1.3);
		this.newRestHeight = this.newHeight2 - this.height;
	}

	handleThrotteledScroll() {
		if (!this.active) {
			return;
		}
		this.checkNextPartModule();
	}

	handleScroll() {
		if (!this.active) {
			return;
		}

		document.body.classList.add('has-scrolled');
		if(window.scrollY>80) {
			if(this.prevScroll>window.scrollY) {
				document.body.classList.add('has-scrolled-up');
				document.body.classList.remove('has-scrolled-down');
			} else {
				document.body.classList.add('has-scrolled-down');
				document.body.classList.remove('has-scrolled-up');
			}
		}

		this.prevScroll = window.scrollY;

		if (this.chapter.lastChapterPartActive&&!this.lastChapter) {
			return;
		}

		const winScroll = document.body.scrollTop || document.documentElement.scrollTop;

		let overScrollPercentage = 0;
		if (winScroll >= this.height2) {
			const overScroll = winScroll - this.height2;
			overScrollPercentage = (overScroll / this.restHeight) * -100;
		}
		if (winScroll >= this.height) {
			overScrollPercentage = 100;
		}

		let newOverScrollPercentage = 0;
		if (winScroll >= this.newHeight2) {
			const newOverScroll = winScroll - this.newHeight2;
			newOverScrollPercentage = (newOverScroll / this.newRestHeight) * -100;
		}
		if (winScroll >= this.height) {
			newOverScrollPercentage = 100;
		}

		if(this.prevPartAnimation) {
			this.prevPartAnimation.seek(this.prevPartAnimation.duration * (newOverScrollPercentage / 100));
		}
		if(this.lastChapterAnimation) {
			this.lastChapterAnimation.seek(this.lastChapterAnimation.duration * (newOverScrollPercentage / 100));
		}
		if(this.nextPartAnimation) {
			this.nextPartAnimation.seek(this.nextPartAnimation.duration * (overScrollPercentage / 100));
		}
		if(this.progressBarAnimation) {
			this.progressBarAnimation.seek(this.progressBarAnimation.duration * (overScrollPercentage / 100));
		}

		const volume = this.chapter.audio.volume - overScrollPercentage / 100;
		this.chapter.audio.audioElement.volume = volume;

	}

	checkNextPartModule() {
		// Check which module is closest to the top of the viewport
		let hasMatch = false;
		for (let index = 0; !hasMatch; index++) {
			const partModule = this.partModulesElms[index];
			if(partModule) {
				const percentage = getViewPercentage(partModule);
				if (percentage > 0) {
					this.activeModule = partModule;
					hasMatch = true;
					break;
				} else {
					continue;
				}
			} else {
				break;
			}
		}
	}

	hide() {
		this.el.classList.remove('is-active');

		if(!this.breakpoint.matches) { // Desktop
			anime.set(this.splitPartNumber[0].chars, {
				opacity: 0
			});
			anime.set(this.splitPartTitle[0].chars, {
				opacity: 0
			});
			anime.remove([this.splitPartNumber[0].chars, this.splitPartTitle[0].chars]);
		} else { // Mobile
			anime.set(this.partNumber, {
				opacity: 0
			});
			anime.set(this.partTitle, {
				opacity: 0
			});
		}

		anime.remove(this.el);
		anime.set(this.el, {
			translateY: this.breakpoint.matches ? window.outerHeight+'px' : window.innerHeight+'px', // Outer
			opacity: 1,
		});
		anime.remove(this.nextPart);
		anime.set(this.nextPart, {
			translateY: this.breakpoint.matches ? window.outerHeight+'px' : window.innerHeight+'px', // Outer
			opacity: 1,
		});

		anime.set(this.partIntrotype, {
			opacity: 0,
		});

		this.introTypePartHeader.classList.remove('is-hidden');
		this.titlesShown = false;

		this.destroy();
	}

	get active() {
		return this.el.classList.contains('is-active');
	}

	destroy() {
		document.removeEventListener('scroll', this.boundScrollListener);
		document.removeEventListener('scroll', this.boundThrotteledScrollListener);
		window.removeEventListener('resize', this.boundResizeListener);

		document.body.classList.remove('has-scrolled');
		document.body.classList.remove('has-scrolled-up');
		document.body.classList.remove('has-scrolled-down');

		// Remove part when finished.. ?
		if(this.breakpoint.matches) {
			this.el.remove();
			this.partIntrotype.remove();
		}
	}
}
